/**
 * VergleichSection — Tab 4b (KI-Offertvergleich + Empfehlung; P2 Placement Slice 11). Replaces the
 * Slice-10 plt4-vergleich placeholder. Renders the matrix, one card per offer, and the recommendation
 * panel; drives the run / re-score / ki-neu / export / freigabe mutations.
 */

import { useState } from 'react';
import {
  COMPARISON_REPORT_STATUS_LABELS,
  VERALTET_GRUND_LABELS,
  VERGLEICH_KEIN_BERICHT_HINWEIS,
  VERGLEICH_VERALTET_HINWEIS,
  VERGLEICH_ZU_WENIG_HINWEIS,
  VERGLEICH_ZUSTELLUNG_OFFEN_HINWEIS,
  type OfferComparisonPatchBody,
  type OfferScoreGewichte,
} from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import {
  useExportVergleich,
  useFreigebenVergleich,
  useKiNeuVergleich,
  usePatchVergleich,
  usePlacementVergleich,
  useRunVergleich,
} from './usePlacementVergleich';
import { VergleichMatrix } from './VergleichMatrix';
import { VergleichOffertKarte } from './VergleichOffertKarte';
import { VergleichEmpfehlungPanel } from './VergleichEmpfehlungPanel';
import { VergleichGewichteDialog } from './VergleichGewichteDialog';

function errCode(e: unknown): string | null {
  if (!e) return null;
  const anyE = e as { code?: string; message?: string };
  return anyE.code ?? anyE.message ?? String(e);
}

export function VergleichSection({ placementId, canWrite }: { placementId: string; canWrite: boolean }) {
  const { data } = usePlacementVergleich(placementId);
  const run = useRunVergleich(placementId);
  const patch = usePatchVergleich(placementId);
  const kiNeu = useKiNeuVergleich(placementId);
  const freigeben = useFreigebenVergleich(placementId);
  const exportDocx = useExportVergleich(placementId);
  const [dialog, setDialog] = useState<'run' | 'rescore' | null>(null);

  if (!data) return null;
  const { bericht } = data;
  const rid = bericht?.id;

  const doRun = (gewichte?: OfferScoreGewichte): void => {
    setDialog(null);
    run.mutate(gewichte ? { gewichte } : {});
  };
  const doRescore = (gewichte: OfferScoreGewichte): void => {
    setDialog(null);
    if (rid) patch.mutate({ rid, body: { gewichte } });
  };
  const doPatch = (body: OfferComparisonPatchBody): void => {
    if (rid) patch.mutate({ rid, body });
  };

  const mutationError =
    errCode(run.error) ?? errCode(patch.error) ?? errCode(freigeben.error) ?? errCode(exportDocx.error) ?? errCode(kiNeu.error);

  return (
    <section className="rounded-lg border border-border p-4" data-testid="placement-vergleich-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium">Vergleich &amp; Empfehlung</h3>
          {bericht && <Badge variant="neutral">{COMPARISON_REPORT_STATUS_LABELS[bericht.status]} (Lauf {bericht.lauf_nr})</Badge>}
          {bericht?.generator.is_demo && <Badge variant="neutral">Demo-KI (Fixture)</Badge>}
        </div>
        <div className="flex gap-2">
          {canWrite && data.ausfuehren_erlaubt && (
            <>
              <Button size="sm" onClick={() => setDialog('run')} data-testid="vergleich-ausfuehren">
                {bericht ? 'Vergleich erneut ausfuehren' : 'Vergleich ausfuehren'}
              </Button>
            </>
          )}
          {canWrite && data.bearbeiten_erlaubt && bericht && (
            <Button variant="outline" size="sm" onClick={() => setDialog('rescore')} data-testid="vergleich-gewichtung">Gewichtung</Button>
          )}
        </div>
      </div>

      {!data.ausfuehren_erlaubt && data.sperrgrund && (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="vergleich-sperrgrund">
          {data.sperrgrund === 'zu_wenig_offerten' && `${VERGLEICH_ZU_WENIG_HINWEIS} (${data.anzahl_normalisiert} von 2 normalisiert)`}
          {data.sperrgrund === 'placement_locked' && 'Ausschreibung abgeschlossen - Vergleich nur lesend'}
          {data.sperrgrund === 'placement_status_invalid' && 'Vergleich erst nach dem Versand der Offertanfragen moeglich'}
          {data.sperrgrund === 'generierung_laeuft' && 'Die KI-Bewertung wird erstellt - die Matrix und der Score sind bereits verfuegbar.'}
          {data.sperrgrund === 'zustellung_offen' && VERGLEICH_ZUSTELLUNG_OFFEN_HINWEIS}
        </p>
      )}

      {bericht?.veraltet && (
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900" data-testid="vergleich-veraltet-banner">
          {VERGLEICH_VERALTET_HINWEIS}
          {bericht.veraltet_grund && <span> ({VERALTET_GRUND_LABELS[bericht.veraltet_grund]})</span>}
          {canWrite && data.ausfuehren_erlaubt && (
            <Button variant="outline" size="sm" className="ml-2" onClick={() => setDialog('run')} data-testid="vergleich-veraltet-rerun">Vergleich erneut ausfuehren</Button>
          )}
        </div>
      )}

      {!bericht && <p className="mt-3 text-sm text-muted-foreground" data-testid="vergleich-empty">{VERGLEICH_KEIN_BERICHT_HINWEIS}</p>}

      {bericht && (
        <div className="mt-3 flex flex-col gap-3">
          <VergleichMatrix bericht={bericht} empfehlungOfferId={bericht.empfehlung_offer_id} />
          <div className="flex flex-col gap-2">
            {bericht.offerten.map((item) => (
              <VergleichOffertKarte
                key={item.offer_document_id}
                item={item}
                bericht={bericht}
                canEdit={canWrite && data.bearbeiten_erlaubt}
                onPatch={(p) => doPatch({ offerten: [p] })}
                saving={patch.isPending}
              />
            ))}
          </div>
          <VergleichEmpfehlungPanel
            placementId={placementId}
            bericht={bericht}
            canEdit={canWrite && data.bearbeiten_erlaubt}
            canRelease={canWrite && data.freigeben_erlaubt}
            onPatch={doPatch}
            onFreigeben={() => rid && freigeben.mutate({ rid })}
            onExport={() => rid && exportDocx.mutate({ rid })}
            onKiNeu={() => rid && kiNeu.mutate({ rid })}
            saving={patch.isPending}
            exporting={exportDocx.isPending}
            releasing={freigeben.isPending}
            error={mutationError}
          />
        </div>
      )}

      <VergleichGewichteDialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        gewichte={bericht?.gewichte ?? data.gewichte_tenant}
        tenantGewichte={data.gewichte_tenant}
        onApply={(g) => (dialog === 'rescore' ? doRescore(g) : doRun(g))}
        saving={run.isPending || patch.isPending}
      />
    </section>
  );
}
